import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { EditableItemList } from '@/components/EditableItemList';
import { SmartSuggestion } from '@/components/SmartSuggestion';
import { FoodSafetyAlert } from '@/components/FoodSafetyAlert';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Check, Loader2, Lock, ArrowRight, Sparkles, Mic, Search, X, ScanLine } from 'lucide-react';

import { getUIConfig } from '@/config/uiConfig';

export function ResultPanel({
  imagePreview, imageFile, analysis, totals, confidence, accuracyDelta,
  editableItems, setEditableItems, suggestion, setSuggestion,
  questions, mealData, voiceEditListening, voiceEditProcessing,
  startVoiceEdit, stopVoiceEdit, handleStartFollowUp, handleConfirm,
  confirmError, showDebugCompress, debugLoading, navigate,
}) {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { user } = useAuth();
  const uiConfig = getUIConfig(user?.experience_mode, user?.expertise_level);

  return (
    <div className="animate-slide-up-spring">
      {/* Inline Upgrade Modal — never navigates away */}
      {showUpgradeModal && (
        <UpgradeModal onClose={() => setShowUpgradeModal(false)} navigate={navigate} />
      )}
      {imagePreview && (
        <div className="relative mb-2">
          <img src={imagePreview} alt="Meal" className="w-full aspect-[4/3] object-cover rounded-2xl" />
        </div>
      )}

      {imageFile && (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
          <button data-testid="debug-preview-btn-result" onClick={showDebugCompress} disabled={debugLoading}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 16, border: '1px dashed #C67A5C', background: 'rgba(198,122,92,0.05)', cursor: 'pointer', fontSize: 11, color: '#C67A5C', fontFamily: 'var(--font-sans)', fontWeight: 500 }}>
            {debugLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
            View image sent to AI
          </button>
        </div>
      )}

      <div className="rounded-[16px] p-6 mb-4" style={{ background: '#FFFFFF', border: '1px solid #E8E0D8', boxShadow: '0 4px 20px -4px rgba(0,0,0,0.06)' }} data-testid="result-panel">
        <div className="text-center mb-6">
          <div className="text-5xl font-bold" style={{ color: '#2C2C2C', transition: 'all 0.3s ease' }}>{Math.round(totals.calories)}</div>
          <div className="text-sm" style={{ color: '#9A8F87' }}>calories</div>
          {accuracyDelta !== 0 && (
            <div data-testid="accuracy-delta" className="inline-flex items-center mt-2 px-3 py-1 rounded-full text-sm font-semibold" style={{ background: 'rgba(198,122,92,0.10)', color: '#C67A5C' }}>
              {accuracyDelta > 0 ? '+' : ''}{Math.round(accuracyDelta)} cal from follow-ups
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { label: 'Protein', val: totals.protein, color: '#4A90D9', desc: 'Builds & repairs muscle' },
            { label: 'Carbs', val: totals.carbs, color: '#E5A84B', desc: 'Your main energy source' },
            { label: 'Fat', val: totals.fat, color: '#D4736A', desc: 'Supports brain & hormones' },
          ].map(m => (
            <div key={m.label} className="text-center">
              <div className="text-2xl font-bold" style={{ color: m.color, transition: 'all 0.3s ease' }}>{Math.round(m.val)}g</div>
              <div className="text-xs" style={{ color: '#9A8F87' }}>{m.label}</div>
              {uiConfig.showHelperLinks && (
                <div className="text-[9px] mt-1" style={{ color: '#B0A99D' }}>{m.desc}</div>
              )}
            </div>
          ))}
        </div>

        {/* Pro: Show full nutrition breakdown inline */}
        {uiConfig.showAdvancedMetrics && analysis?.micronutrients && (
          <div data-testid="micronutrient-table" className="mb-4 p-3 rounded-xl" style={{ background: 'rgba(26,58,74,0.03)', border: '1px solid rgba(26,58,74,0.06)' }}>
            <p className="text-[10px] font-semibold mb-2" style={{ color: '#7A7A72', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Micronutrients</p>
            <div className="grid grid-cols-3 gap-x-4 gap-y-1">
              {Object.entries(analysis.micronutrients).filter(([, v]) => v > 0).slice(0, 9).map(([key, val]) => (
                <div key={key} className="flex justify-between text-[10px]">
                  <span style={{ color: '#9A8F87' }}>{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/ G$/, '').replace(/ Mg$/, '').replace(/ Mcg$/, '')}</span>
                  <span style={{ fontWeight: 600, color: '#1A3A4A' }}>{Math.round(val)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Beginner: Explain what the numbers mean */}
        {uiConfig.showEducationalCards && (
          <div data-testid="beginner-explanation" className="mb-4 p-3 rounded-xl" style={{ background: 'rgba(107,158,138,0.06)', border: '1px solid rgba(107,158,138,0.12)' }}>
            <p className="text-xs font-medium" style={{ color: '#5A8C6A', lineHeight: 1.5 }}>
              This meal has {Math.round(totals.calories)} calories. {Math.round(totals.protein)}g of protein will help your muscles recover. {Math.round(totals.carbs)}g of carbs gives you energy, and {Math.round(totals.fat)}g of fat supports your brain and hormones.
            </p>
          </div>
        )}

        <div className="mb-4">
          <EditableItemList items={editableItems} onItemsChange={setEditableItems} />
        </div>

        <VoiceEditButton listening={voiceEditListening} processing={voiceEditProcessing}
          onStart={startVoiceEdit} onStop={stopVoiceEdit} />

        <div className="flex items-center justify-center gap-2" data-testid="confidence-badge">
          {analysis?.scale_detected && analysis?.scale_reading && (
            <span className="text-xs font-semibold px-3 py-1.5 rounded-full" style={{ background: 'rgba(90,140,106,0.10)', color: '#5A8C6A' }} data-testid="scale-verified-badge">
              {'\u2696\uFE0F'} Scale-Verified: {analysis.scale_reading.value}{analysis.scale_reading.unit}
            </span>
          )}
          <div className="px-4 py-1.5 rounded-full text-sm font-semibold" style={{
            background: confidence >= 85 ? 'rgba(90,140,106,0.10)' : confidence >= 60 ? 'rgba(229,168,75,0.10)' : 'rgba(198,122,92,0.10)',
            color: confidence >= 85 ? '#5A8C6A' : confidence >= 60 ? '#E5A84B' : '#C67A5C',
          }}>
            {Math.round(confidence)}% accuracy
          </div>
        </div>
      </div>

      {suggestion && (
        <div className="mb-4">
          <SmartSuggestion suggestion={suggestion} onDismiss={() => setSuggestion(null)} />
        </div>
      )}

      {editableItems.length > 0 && (
        <div className="mb-4">
          <FoodSafetyAlert items={editableItems.map(i => ({ name: i.name || i.food_name }))} />
        </div>
      )}

      <div className="space-y-3">
        {confirmError && (
          <div data-testid="confirm-error-banner" style={{ padding: '14px 18px', borderRadius: 14, background: 'rgba(198,122,92,0.08)', border: '1.5px solid #C67A5C', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#C67A5C', fontFamily: 'var(--font-sans)' }}>Something went wrong saving your meal</p>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9A8F87', fontFamily: 'var(--font-sans)' }}>Tap retry to try again</p>
            </div>
            <button data-testid="confirm-retry-btn" onClick={handleConfirm} style={{ padding: '8px 18px', borderRadius: 20, border: 'none', cursor: 'pointer', background: '#C67A5C', color: '#FFF', fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-sans)' }}>Retry</button>
          </div>
        )}
        {questions.length > 0 && !mealData?.follow_up_answers?.length && (
          mealData?.follow_up_locked ? (
            <div data-testid="upgrade-prompt-followup" onClick={() => setShowUpgradeModal(true)}
              className="w-full p-4 rounded-xl cursor-pointer transition-all" style={{ background: 'rgba(198,122,92,0.06)', border: '1px solid rgba(198,122,92,0.15)' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(198,122,92,0.10)' }}>
                  <Lock className="w-5 h-5" style={{ color: '#C67A5C' }} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold" style={{ color: '#2C2C2C' }}>Answer {questions[0]?.question_count || 2} questions to improve accuracy from 68% to 94%</p>
                  <p className="text-xs font-medium mt-0.5" style={{ color: '#C67A5C' }}>Upgrade to Pro to unlock</p>
                </div>
                <ArrowRight className="w-4 h-4" style={{ color: '#C67A5C' }} />
              </div>
            </div>
          ) : (
            <Button data-testid="improve-accuracy-btn" onClick={handleStartFollowUp}
              className="w-full h-14 rounded-xl text-white font-semibold text-base" style={{ background: '#C67A5C', border: 'none' }}>
              <Sparkles className="w-5 h-5 mr-2" /> Improve Accuracy
            </Button>
          )
        )}
        <LabelScanButton navigate={navigate} mealData={mealData} />
        <Button data-testid="confirm-meal-btn" onClick={handleConfirm}
          className="w-full h-14 rounded-xl hover:opacity-90 text-white font-semibold text-base" style={{ background: '#2C2C2C', border: 'none' }}>
          <Check className="w-5 h-5 mr-2" /> Confirm & Log
        </Button>
        <button data-testid="adjust-btn" onClick={() => navigate('/dashboard')}
          className="w-full text-center text-sm font-medium py-2" style={{ color: '#9A8F87', background: 'none', border: 'none', cursor: 'pointer' }}>
          Discard
        </button>
      </div>

      <style>{`
        @keyframes voiceEditPulse {
          0%, 100% { border-color: #C67A5C; box-shadow: 0 0 0 4px rgba(198,122,92,0.10); }
          50% { border-color: #C67A5C; box-shadow: 0 0 0 8px rgba(198,122,92,0.15); }
        }
      `}</style>
    </div>
  );
}

function VoiceEditButton({ listening, processing, onStart, onStop }) {
  return (
    <div className="mb-6" data-testid="voice-edit-container">
      {/* Instructional banner — always visible when idle */}
      {!listening && !processing && (
        <div data-testid="voice-edit-instruction" style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px',
          borderRadius: '14px 14px 0 0', background: '#FFF8F4',
          border: '1px solid rgba(198,122,92,0.15)', borderBottom: 'none',
        }}>
          <Mic className="w-4 h-4 flex-shrink-0" style={{ color: '#C67A5C', marginTop: 1 }} />
          <div>
            <p style={{ fontSize: 13, fontWeight: 600, color: '#2C2C2C', margin: 0, fontFamily: 'var(--font-sans)' }}>
              Wrong portion? Missing something?
            </p>
            <p style={{ fontSize: 12, color: '#9A8F87', margin: '2px 0 0', fontFamily: 'var(--font-sans)', lineHeight: 1.4 }}>
              Press and hold the button below, then say what to fix. Example: "That was a large coffee, not small" or "Add a side of fries"
            </p>
          </div>
        </div>
      )}

      <button data-testid="voice-edit-btn"
        onMouseDown={!processing ? onStart : undefined}
        onMouseUp={listening ? onStop : undefined}
        onTouchStart={!processing ? (e => { e.preventDefault(); onStart(); }) : undefined}
        onTouchEnd={listening ? (e => { e.preventDefault(); onStop(); }) : undefined}
        disabled={processing}
        style={{
          width: '100%', height: 64, 
          borderRadius: !listening && !processing ? '0 0 14px 14px' : 14,
          background: processing ? '#F5F0EB' : listening ? '#C67A5C' : '#FFFFFF',
          border: processing ? '1px solid #E8E0D8' : listening ? '2px solid #C67A5C' : '2px solid #C67A5C',
          cursor: processing ? 'wait' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
          transition: 'all 0.2s ease',
          position: 'relative', overflow: 'hidden',
          WebkitUserSelect: 'none', userSelect: 'none',
          WebkitTouchCallout: 'none',
        }}>

        {/* Pulse ring animation when listening */}
        {listening && (
          <>
            <div style={{
              position: 'absolute', inset: -4, borderRadius: 18,
              border: '2px solid rgba(198,122,92,0.2)',
              animation: 'voiceRingPulse 1.5s ease-out infinite',
            }} />
            <div style={{
              position: 'absolute', inset: -8, borderRadius: 22,
              border: '2px solid rgba(198,122,92,0.1)',
              animation: 'voiceRingPulse 1.5s ease-out infinite 0.3s',
            }} />
          </>
        )}

        {processing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#9A8F87' }} />
            <span style={{ fontSize: 15, fontWeight: 500, color: '#9A8F87', fontFamily: 'var(--font-sans)' }}>
              Updating your meal...
            </span>
          </>
        ) : listening ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Mic className="w-6 h-6" style={{ color: '#FFFFFF' }} />
              {/* Animated sound wave bars */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginLeft: 4 }}>
                {[0, 1, 2, 3].map(i => (
                  <div key={i} style={{
                    width: 3, borderRadius: 2, background: '#FFFFFF',
                    animation: `soundWave 0.6s ease-in-out infinite ${i * 0.1}s`,
                  }} />
                ))}
              </div>
            </div>
            <span style={{ fontSize: 16, fontWeight: 600, color: '#FFFFFF', fontFamily: 'var(--font-sans)' }}>
              Listening... release when done
            </span>
          </>
        ) : (
          <>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: 'rgba(198,122,92,0.08)', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}>
              <Mic className="w-5 h-5" style={{ color: '#C67A5C' }} />
            </div>
            <span style={{ fontSize: 16, fontWeight: 600, color: '#C67A5C', fontFamily: 'var(--font-sans)' }}>
              Hold to Edit with Voice
            </span>
          </>
        )}
      </button>

      <style>{`
        @keyframes voiceRingPulse {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(1.08); opacity: 0; }
        }
        @keyframes soundWave {
          0%, 100% { height: 8px; }
          50% { height: 20px; }
        }
      `}</style>
    </div>
  );
}

/* ── Label Scan Button ── */
function LabelScanButton({ navigate, mealData }) {
  const fileRef = React.useRef(null);
  const [scanning, setScanning] = useState(false);
  const { token } = useAuth();

  const handleScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);

    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result;
        const res = await fetch('/api/label-scan/scan', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            image_base64: base64,
            meal_id: mealData?.meal_id || null,
          }),
        });
        if (res.ok) {
          const data = await res.json();
          // Navigate to label comparison view with scan data
          navigate('/label-compare', { state: { scanResult: data, mealData } });
        } else {
          const { toast } = await import('sonner');
          toast.error('Could not read label. Try a clearer photo.');
        }
        setScanning(false);
      };
      reader.readAsDataURL(file);
    } catch {
      setScanning(false);
    }
  };

  return (
    <div className="mb-3">
      <input ref={fileRef} type="file" accept="image/*" capture="environment" data-testid="label-scan-input"
        onChange={handleScan} style={{ display: 'none' }} />
      <button
        data-testid="scan-label-btn"
        onClick={() => fileRef.current?.click()}
        disabled={scanning}
        style={{
          width: '100%', height: 48, borderRadius: 12,
          background: '#FFFFFF', border: '1px solid #E8E0D8',
          cursor: scanning ? 'wait' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          transition: 'all 0.2s',
          opacity: scanning ? 0.6 : 1,
        }}
      >
        {scanning ? (
          <><Loader2 className="w-4 h-4 animate-spin" style={{ color: '#C67A5C' }} />
          <span style={{ fontSize: 14, fontWeight: 500, color: '#9A8F87', fontFamily: 'var(--font-sans)' }}>Reading label...</span></>
        ) : (
          <><ScanLine className="w-4 h-4" style={{ color: '#C67A5C' }} />
          <span style={{ fontSize: 14, fontWeight: 500, color: '#6A6560', fontFamily: 'var(--font-sans)' }}>Scan Nutritional Label</span></>
        )}
      </button>
    </div>
  );
}



/* ── Inline Upgrade Modal (never navigates away from meal flow) ── */
function UpgradeModal({ onClose }) {
  const { authHeaders } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const res = await axios.post('/api/stripe/create-checkout', {
        plan_id: 'pro',
        origin_url: window.location.origin,
      }, authHeaders());
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="upgrade-modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
    >
      <div
        data-testid="upgrade-modal-sheet"
        onClick={e => e.stopPropagation()}
        className="animate-slide-up-spring"
        style={{
          width: '100%', maxWidth: 440,
          background: '#FFFDF9', borderRadius: '24px 24px 0 0',
          padding: '28px 24px 36px', position: 'relative',
          boxShadow: '0 -8px 40px rgba(0,0,0,0.12)',
        }}
      >
        {/* Close handle */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, background: '#E8E0D8' }} />
        </div>

        <button
          data-testid="upgrade-modal-close"
          onClick={onClose}
          style={{
            position: 'absolute', top: 16, right: 16, background: 'none',
            border: 'none', cursor: 'pointer', padding: 4,
          }}
        >
          <X className="w-5 h-5" style={{ color: '#9A8F87' }} />
        </button>

        {/* Icon */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 16,
            background: 'rgba(198,122,92,0.10)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Sparkles className="w-7 h-7" style={{ color: '#C67A5C' }} />
          </div>
        </div>

        {/* Heading */}
        <h2
          data-testid="upgrade-modal-heading"
          style={{
            fontFamily: 'var(--font-serif)', fontSize: 24, fontWeight: 400,
            color: '#2C2C2C', textAlign: 'center', margin: '0 0 8px',
            lineHeight: 1.3,
          }}
        >
          Unlock Smart Accuracy
        </h2>

        {/* Body */}
        <p
          style={{
            fontFamily: 'var(--font-sans)', fontSize: 14, lineHeight: 1.5,
            color: '#6A6560', textAlign: 'center', margin: '0 0 24px',
          }}
        >
          Follow-up questions catch 200-500 hidden calories per meal that photo-only apps miss.
        </p>

        {/* Upgrade Button */}
        <button
          data-testid="upgrade-modal-cta"
          onClick={handleUpgrade}
          disabled={loading}
          style={{
            width: '100%', padding: 16, borderRadius: 28, border: 'none',
            background: '#C67A5C', color: '#FFF', cursor: loading ? 'wait' : 'pointer',
            fontSize: 16, fontWeight: 600, fontFamily: 'var(--font-sans)',
            boxShadow: '0 4px 16px rgba(198,122,92,0.25)',
            transition: 'transform 0.15s, box-shadow 0.15s',
            opacity: loading ? 0.7 : 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
          onMouseOver={e => { if (!loading) e.currentTarget.style.transform = 'scale(1.02)'; }}
          onMouseOut={e => { e.currentTarget.style.transform = 'scale(1)'; }}
        >
          {loading && <Loader2 className="w-5 h-5 animate-spin" />}
          {loading ? 'Opening Checkout...' : 'Upgrade to Pro \u2014 $14.99/mo'}
        </button>

        {/* Maybe Later */}
        <button
          data-testid="upgrade-modal-dismiss"
          onClick={onClose}
          style={{
            width: '100%', padding: 12, background: 'none', border: 'none',
            cursor: 'pointer', fontSize: 14, fontWeight: 500,
            color: '#9A8F87', fontFamily: 'var(--font-sans)',
            marginTop: 8,
          }}
        >
          Maybe later
        </button>
      </div>
    </div>
  );
}
